// @flow

import React, { Fragment } from 'react';
import moment from 'moment';
import map from 'lodash/map';
import NavLink from 'hoc/portalPath/NavLink';
import ShowAt from 'components/Viewport/ShowAt';
import HideAt from 'components/Viewport/HideAt';
import type { L10n } from 'type/type';
import { formatedDuration, formatedDurationByMilliseconds } from 'services/format/formatDuration';
import ClaimTool from 'components/ClaimTool/ClaimTool';
import { parse, stringify } from 'query-string';

import StagedResult from './StagedResult';

const getGapTime = (finish, top) => {
  const finishMoment = moment.duration(finish);
  const topMoment = moment.duration(top);
  return formatedDurationByMilliseconds(finishMoment.valueOf() - topMoment.valueOf());
};

export const formatUserInfo = (gender: string, age: number, l10n: L10n) => {
  if (gender && age) {
    return `${gender} | ${l10n.formatMessage('global.participant.age')} ${age}`;
  } if (gender) {
    return gender;
  } if (age) {
    return `${l10n.formatMessage('global.participant.age')} ${age}`;
  }
  return '';
};

type resultType = {
  id: string,
  bib: string,
  person: Object,
  finishTime: string,
  resultId: number,
  routeStageResultList: [],
  participantNumber: string,
}

type propsType = {
  rankTopTime: string,
  index: number,
  item: resultType,
  l10n: L10n,
  isKioskMode: boolean,
  queryString: string
}

const ResultItem = (props: propsType) => {
  const {
    item,
    index,
    rankTopTime,
    l10n,
    queryString,
    isKioskMode,
  } = props;
  const {
    firstName, lastName, gender, age, enterprisePersonId,
  } = item.person;

  const queryParams = {
    pn: item.participantNumber,
    ...(isKioskMode ? parse(queryString) : {}),
  };

  const personName = (
    <NavLink to={`/participants/${item.id}?${stringify(queryParams)}`}>
      {`${firstName} ${lastName}`}
    </NavLink>
  );

  const resultHasBeenClaimed = enterprisePersonId && !enterprisePersonId.match(/^NAP_/);
  const claimToolText = l10n.formatMessage('global.claim');
  const claimToolparams = {
    resultHasBeenClaimed,
    resultId: item.resultId,
    text: claimToolText,
    lastName,
    participantId: item.id,
    isKioskMode,
  };
  const renderClaimTool = () => <ClaimTool {...claimToolparams} />;

  return (
    <div className="event-home__item">
      <ShowAt smAndAbove>
        <Fragment>
          <div className="event-home__rank">{index + 1}</div>
          <StagedResult
            containerClassName="event-home__bib"
            result={item.bib || l10n.formatMessage('page.event.NA')}
            info={l10n.formatMessage('global.participant.bib')} />
          <StagedResult
            containerClassName="event-home__person"
            result={personName}
            info={formatUserInfo(gender, age, l10n)} />
        </Fragment>
      </ShowAt>
      <ShowAt mdAndAbove>
        <div className="event-home__empty">
          <ShowAt xlAndAbove>
            {
              item.routeStageResultsSize > 1 ? map(item.routeStageResultList, stageResult => (
                <StagedResult
                  id={stageResult.id}
                  key={stageResult.id}
                  containerClassName="event-home__bib"
                  result={stageResult.finishTime ? formatedDuration(stageResult.finishTime) : '--'}
                  info={stageResult.name} />
              )) : null
            }
          </ShowAt>
        </div>
      </ShowAt>
      <ShowAt smAndAbove>
        <Fragment>
          <StagedResult
            containerClassName="event-home__finish"
            result={formatedDuration(item.finishTime)}
            info={l10n.formatMessage('page.event.finish')} />
          <StagedResult
            containerClassName="event-home__finish"
            result={index === 0 ? '' : `+ ${getGapTime(item.finishTime, rankTopTime)}`}
            info={index === 0 ? '' : l10n.formatMessage('page.event.gap')} />
          { renderClaimTool() }
        </Fragment>
      </ShowAt>
      <HideAt smAndAbove>
        <div className="event-home__userInfo">
          <div className="event-home__rank">{index + 1}</div>
          <StagedResult
            containerClassName="event-home__person"
            result={personName}
            info={`Bib # ${item.bib} | ${formatUserInfo(gender, age, l10n)}`} />
        </div>
        <div className="event-home__score">
          <StagedResult
            containerClassName="event-home__finish"
            result={formatedDuration(item.finishTime)}
            info={l10n.formatMessage('page.event.finish')} />
          <StagedResult
            containerClassName="event-home__finish"
            result={index === 0 ? '' : `+ ${getGapTime(item.finishTime, rankTopTime)}`}
            info={index === 0 ? '' : l10n.formatMessage('page.event.gap')} />
        </div>
        { renderClaimTool() }
      </HideAt>
    </div>
  );
};

export default ResultItem;
